import _ from 'lodash'
import { State } from './'
import { MediaData } from 'components/MediaViewer'
import { BaseAction } from './index'
import { COUNTRY_ENUM } from 'services/api/constants'
import { DisableBannerResponse } from 'services/api/banners'
import { ERROR_CODES } from './error'

export enum BANNER_VIEW_TYPE {
    BANNER_GROUPS = 0,
    BANNERS,
    BANNER_EDITOR,
}

export type BannerManagerViews = BANNER_VIEW_TYPE

export enum DEEPLINK_TYPE {
    CAMPAIGNS = 0,
    PROFILE,
    SUBMISSION,
    POLL,
    TASK_LIST,
    EXTERNAL,
}

export enum BANNER_STATUS {
    LIVE = 0,
    SCHEDULED,
    ENDED,
    DISABLED,
}

export type BannerGroupState = {
    uuid: string
    name: string
    country: COUNTRY_ENUM
}

export type TimeWindowState = {
    start: string
    end: string
}

export type BannerState = {
    uuid: string
    name: string
    image: MediaData
    time_window: TimeWindowState
    deeplink_type: DEEPLINK_TYPE
    deeplink_data?: string
    disabled_at?: string
    status?: BANNER_STATUS
}

type BannerGroupEntitiesState = {
    [uuid: string]: BannerGroupState
}

type BannerEntitiesState = {
    [uuid: string]: BannerState
}

export type BannerManagerState = {
    byId: {
        bannerGroups: BannerGroupEntitiesState
        banners: BannerEntitiesState
    }
    bannerGroups: string[]
    banners: string[]
}

const initialState: BannerManagerState = {
    byId: {
        bannerGroups: {},
        banners: {},
    },
    bannerGroups: [],
    banners: [],
}

export enum BannerManagerActions {
    BANNER_GROUPS_REQUEST = 'banners/BANNER_GROUPS_REQUEST',
    BANNERS_REQUEST = 'banners/BANNERS_REQUEST',
    SAVE_BANNER_GROUPS = 'banners/SAVE_BANNER_GROUPS',
    SAVE_BANNERS = 'banners/SAVE_BANNERS',
    CLEAR_BANNER_GROUPS = 'banners/CLEAR_BANNER_GROUPS',
    CLEAR_BANNERS = 'banners/CLEAR_BANNERS',
    CREATE_BANNER_REQUEST = 'banners/CREATE_BANNER_REQUEST',
    DISABLE_BANNER_REQUEST = 'banners/DISABLE_BANNER_REQUEST',
    UPDATE_BANNER = 'banners/UPDATE_BANNER',
}

export const getBannerGroups = () => ({
    type: BannerManagerActions.BANNER_GROUPS_REQUEST,
})

export interface BannerPayload {
    bannerGroupUuid: string
    offset?: number
}

export const getBanners = (payload: BannerPayload) => ({
    payload,
    type: BannerManagerActions.BANNERS_REQUEST,
})

export const saveBannerGroups = (payload?: BannerGroupState[]) => ({
    payload,
    type: BannerManagerActions.SAVE_BANNER_GROUPS,
})

export const saveBanners = (payload?: BannerState[]) => ({
    payload,
    type: BannerManagerActions.SAVE_BANNERS,
})

export const clearBannerGroups = () => ({
    type: BannerManagerActions.CLEAR_BANNER_GROUPS,
})

export const clearBanners = () => ({
    type: BannerManagerActions.CLEAR_BANNERS,
})

// please adjust this type for necesarry cb param type by using union
type TCallbackParams = (ERROR_CODES | undefined)[]

export interface CreateBannerPayload {
    name: string
    image: MediaData
    time_window: TimeWindowState
    deeplink_type: DEEPLINK_TYPE
    deeplink_data?: string
    bannerGroupUuid: string
    callback: (...args: TCallbackParams) => void
}

export const createBanner = (payload: CreateBannerPayload) => ({
    payload,
    type: BannerManagerActions.CREATE_BANNER_REQUEST,
})

export interface DisableBannerPayload {
    bannerGroupUuid: string
    bannerUuid: string
}

export const disableBanner = (payload: DisableBannerPayload) => ({
    payload,
    type: BannerManagerActions.DISABLE_BANNER_REQUEST,
})

export const updateBanner = (payload?: DisableBannerResponse | BannerState) => ({
    payload,
    type: BannerManagerActions.UPDATE_BANNER,
})

export const bannerManagerReducer = (state: BannerManagerState = initialState, action: BaseAction) => {
    switch (action.type) {
        case BannerManagerActions.SAVE_BANNER_GROUPS: {
            const { payload } = action
            const uuids = payload.map((bannerGroup: BannerGroupState) => bannerGroup.uuid)
            const byId = _.keyBy(payload, 'uuid')
            return Object.assign({}, state, {
                ...state,
                byId: {
                    ...state.byId,
                    bannerGroups: byId,
                },
                bannerGroups: uuids,
            })
        }
        case BannerManagerActions.SAVE_BANNERS: {
            const { payload } = action
            const uuids = payload.map((banner: BannerState) => banner.uuid)
            const byId = _.keyBy(payload, 'uuid')
            return Object.assign({}, state, {
                ...state,
                byId: {
                    ...state.byId,
                    banners: byId,
                },
                banners: uuids,
            })
        }
        case BannerManagerActions.UPDATE_BANNER: {
            const { payload } = action
            return Object.assign({}, state, {
                ...state,
                byId: {
                    ...state.byId,
                    banners: Object.assign({}, state.byId.banners, {
                        [payload.uuid]: { ...state.byId.banners[payload.uuid], ...payload },
                    }),
                },
            })
        }
        case BannerManagerActions.CLEAR_BANNER_GROUPS: {
            return Object.assign({}, state, {
                ...state,
                byId: {
                    ...state.byId,
                    bannerGroups: {},
                },
                bannerGroups: [],
            })
        }
        case BannerManagerActions.CLEAR_BANNERS: {
            return Object.assign({}, state, {
                ...state,
                byId: {
                    ...state.byId,
                    banners: {},
                },
                banners: [],
            })
        }
        default:
            return state
    }
}

export const selectBannerGroups = (state: State) =>
    state.banner.bannerGroups.map((uuid: string) => state.banner.byId.bannerGroups[uuid])

export const selectBanners = (state: State) =>
    state.banner.banners.map((uuid: string) => state.banner.byId.banners[uuid])

export const selectBannerGroupCountry = (bannerGroupUuid: string) => (state: State) =>
    state.banner.byId.bannerGroups[bannerGroupUuid] ? state.banner.byId.bannerGroups[bannerGroupUuid].country : null

export const selectBanner = (bannerUuid?: string | null) => (state: State) =>
    bannerUuid && state.banner.byId.banners[bannerUuid] ? state.banner.byId.banners[bannerUuid] : undefined
